<template>
  <div>
    <!-- Header and Actions -->
    <div
      class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 gap-3">
      <h2 class="text-primary m-0">
        <i class="bi bi-table me-2"></i>
      </h2>

      <div class="d-flex flex-wrap gap-2 w-100 justify-content-md-end">
        <input v-model="searchTerm" type="text" class="form-control me-md-2" placeholder="Search loans..."
          style="max-width: 250px;" />

        <div class="dropdown">
          <button class="btn btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">
            <i class="bi bi-gear"></i> Actions
          </button>
          <ul class="dropdown-menu dropdown-menu-end">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="fetchApprovedLoans" :class="{ disabled: loading }">
                <i class="bi bi-arrow-repeat me-2"></i> Refresh
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportCSV" :class="{ disabled: loading }">
                <i class="bi bi-download me-2"></i> Export CSV
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center my-4">
      <b-spinner label="Loading..."></b-spinner>
    </div>

    <!-- Error -->
    <b-alert v-if="error" variant="danger" dismissible @dismissed="error = ''">
      {{ error }}
    </b-alert>

    <!-- Table -->
    <b-table v-if="!loading" :items="filteredLoans" :fields="fields" :current-page="currentPage" :per-page="perPage"
      :total-rows="filteredLoans.length" :sort-by.sync="sortKey" :sort-desc.sync="sortDesc"
      :empty-text="error || 'No approved loans found.'" responsive hover striped small>
      <!-- Index -->
      <template #cell(index)="data">
        {{ (currentPage - 1) * perPage + data.index + 1 }}
      </template>

      <!-- Applicant -->
      <template #cell(applicant)="data">
        <div class="d-flex align-items-center">
          <img v-if="data.item.fileUrl" :src="`${apiBaseUrl}${data.item.fileUrl}`"
            class="rounded-circle me-2 client-photo-sm" />
          <span>{{ data.item.applicant }}</span>
        </div>
      </template>

      <!-- Principal -->
      <template #cell(principalAmount)="data">
        <span class="text-primary fw-bold">
          {{ formatCurrency(data.item.principalAmount) }}
        </span>
      </template>

      <!-- Payable -->
      <template #cell(totalPayableAmount)="data">
        <span class="text-info fw-semibold">
          {{ formatCurrency(data.item.totalPayableAmount) }}
        </span>
      </template>

      <!-- Paid -->
      <template #cell(paidAmount)="data">
        <span class="text-success fw-bold">
          {{ formatCurrency(data.item.paidAmount) }}
        </span>
      </template>

      <!-- OD -->
      <template #cell(odbalance)="data">
        <span :class="{
          'text-danger fw-bold': data.item.odbalance > 0,
          'text-muted': data.item.odbalance === 0
        }">
          {{ formatCurrency(data.item.odbalance) }}
        </span>
      </template>

      <!-- OB -->
      <template #cell(obbalance)="data">
        <span :class="{
          'text-warning fw-bold': data.item.obbalance > 0,
          'text-success fw-semibold': data.item.obbalance === 0
        }">
          {{ formatCurrency(data.item.obbalance) }}
        </span>
      </template>

      <!-- Status -->
      <template #cell(approvalStatus)="data">
        <span :class="{
          'badge bg-success': data.item.approvalStatus === 'APPROVED',
          'badge bg-warning text-dark': data.item.approvalStatus === 'PENDING',
          'badge bg-primary': data.item.approvalStatus === 'DISBURSED'
        }">
          {{ data.item.approvalStatus }}
        </span>
      </template>

      <!-- Actions -->
      <template #cell(actions)="data">
        <button class="btn btn-sm btn-outline-primary" @click="viewLoan(data.item)">
          <i class="bi bi-eye"></i> View
        </button>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-pagination v-model="currentPage" :total-rows="filteredLoans.length" :per-page="perPage" align="center"
      class="mt-3" />

    <!-- Modal -->
    <LoanDetailsModal v-model="showModal" :loan="selectedLoan" />
  </div>
</template>

<script>
import loanApi from '@/api/loanApi'
import LoanDetailsModal from '@/components/modals/LoanDetailsModal.vue'

export default {
  components: { LoanDetailsModal },

  data() {
    return {
      apiBaseUrl: " ",
      loans: [],
      loading: false,
      error: '',
      searchTerm: '',
      sortKey: 'creationDate',
      sortDesc: true,
      currentPage: 1,
      perPage: 10,
      showModal: false,
      selectedLoan: null,
      fields: [
        { key: 'index', label: '#' },
        { key: 'applicant', label: 'Client Name', sortable: true },
        { key: 'principalAmount', label: 'Principal', sortable: true },
        { key: 'totalPayableAmount', label: 'Payable', sortable: true },
        { key: 'paidAmount', label: 'Paid', sortable: true },
        { key: 'odbalance', label: 'OD', sortable: true },
        { key: 'obbalance', label: 'OB', sortable: true },
        { key: 'approvalStatus', label: 'Status' },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },

  computed: {
    filteredLoans() {
      const term = this.searchTerm.toLowerCase()

      return this.loans.filter(loan =>
        loan.applicant?.toLowerCase().includes(term) ||
        loan.approvalStatus?.toLowerCase().includes(term)
      )
    }
  },

  watch: {
    searchTerm() {
      this.currentPage = 1
    }
  },

  methods: {
    fetchApprovedLoans() {
      this.loading = true
      this.error = ''

      loanApi
        .getAll()
        .then(res => {
          this.loans = res.data
        })
        .catch(() => {
          this.error = 'Failed to load loans.'
        })
        .finally(() => {
          this.loading = false
        })
    },

    viewLoan(loan) {
      localStorage.setItem('clickedmenu', 'New Loan')

      this.$router.push({
        path: '/form',
        query: {
          view: 'true',
          loanId: loan.id
        }
      })
    },

    // viewLoan(loan) {
    //   localStorage.setItem('loanToView', JSON.stringify(loan))
    //   localStorage.setItem('clickedmenu', 'View Loan')
    //   this.$router.push('/display')
    // },

    formatCurrency(value) {
      if (value == null) return '-'
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES'
      }).format(value)
    },

    exportCSV() {
      const headers = [['#', 'Applicant', 'Principal', 'Status']]
      const rows = this.filteredLoans.map((loan, idx) => [
        idx + 1,
        loan.applicant,
        this.formatCurrency(loan.principalAmount),
        loan.approvalStatus
      ])

      const csv = [...headers, ...rows].map(r => r.join(',')).join('\n')
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'approved_loans.csv'
      link.click()
    }
  },

  mounted() {
    this.fetchApprovedLoans()
  }
}
</script>

<style scoped>
.client-photo-sm {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border: 1px solid #ccc;
}
</style>
